import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import DateRangePicker from 'components/DateRangePicker';

import { Form, Row, Col } from 'antd';
import useFormatMessage from 'hooks/useFormatMessage';
import useGlobalContext from 'hooks/useGlobalContext';
import ReportContent from './ReportContent';

import messages from '../messages';

const SAVE_CONTENT = gql`
  mutation EditReport_SaveContent($input: UpdateReportContentInput) {
    updateReportContent(input: $input) {
      id
     }
  }
`;

function toChartSettingsInput(chartSettings) {
  return chartSettings ? {
    type: chartSettings.type,
    order: chartSettings.order,
    displayMode: chartSettings.displayMode,
    switchedAxis: chartSettings.switchedAxis,
    disabledLegends: chartSettings.disabledLegends,
    showPreviousStep: chartSettings.showPreviousStep,
    showDropOff: chartSettings.showDropOff,
    fieldResultType: chartSettings.fieldResultType,
  } : null;
}

function toReportContentInput(chart, index) {
  // Users can only edit dateRange and sortOrder for now.
  return {
    id: chart.id,
    includeAISummary: chart.includeAISummary,
    sortOrder: index,
    chartSettings: toChartSettingsInput(chart.chartSettings),
    contentSettings: {
      chartComment: chart.contentSettings.chartComment,
    },
    // dateRange can be null if not changed for a specific chart.
    filter: chart.filter ? {
      dateRange: chart.filter.dateRange,
    } : undefined,
  };
}

const EditContent = ({ form, onDone, report }) => {
  const [reportDateRange, setReportDateRange] = useState(report.dateRange);
  const [saveContent] = useMutation(SAVE_CONTENT);
  const { message } = useGlobalContext();
  const $t = useFormatMessage();

  const onFinish = async (values) => {
    try {
      const contentInput = values.content.map(toReportContentInput);
      await saveContent({
        variables: {
          input: {
            id: report.id,
            content: contentInput,
            dateRange: values.dateRange,
          },
        },
      });
      message.success($t(messages.contentSavedMessage));
    } catch (e) {
      console.error(e);
      message.error($t(messages.errorSaving));
    }
    if (onDone) {
      onDone();
    }
  };

  const initialValues = {
    dateRange: report.dateRange,
    content: report.content,
  };

  return (
    <Form form={form} className="report-content" initialValues={initialValues} onFinish={onFinish} layout="vertical">
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="dateRange" label={$t(messages.reportDateRangeFieldLabel)}>
            <DateRangePicker onChange={setReportDateRange} placeholder={$t(messages.reportDateRangePlaceholder)} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="content" valuePropName="content">
        <ReportContent reportDateRange={reportDateRange} timeOffset={report.timeOffset} language={report.language} aiDisabled={report.customer.disableAI} />
      </Form.Item>
    </Form>
  );
};

export default EditContent;
